// src/components/ProductModal.js
// Crear/editar un producto del catálogo. Delega el guardado vía onSave(data, dielineFile)
// (mismo patrón que ClientModal/MaterialModal). Los atributos se generan dinámicamente
// según el schema de la categoría elegida (PLAN-INTAKE-PRODUCTOS Fase A).
import React, { useState, useEffect, useMemo } from "react";
import QRCode from "qrcode";
import { ref, uploadBytes, getDownloadURL } from "firebase/storage";
import { getCategory } from "../lib/productCategories";
import { productQrPayload } from "../lib/products";
import { downloadQrSvg, downloadQrPng } from "../utils/qrLabel";
import { MEASURE_UNITS, parseMeasure, convert, round } from "../lib/measure";

const IconQrSmall = () => <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20"><path d="M3 3h6v6H3V3zm2 2v2h2V5H5zM11 3h6v6h-6V3zm2 2v2h2V5h-2zM3 11h6v6H3v-6zm2 2v2h2v-2H5zM13 11h2v2h-2v-2zM15 13h2v2h-2v-2zM11 15h2v2h-2v-2zM15 15h2v2h-2v-2z"></path></svg>;

// Control de dimensiones: Largo × Ancho (× Alto) con unidad (in/cm/m), acepta decimales y
// fracciones (ej. "12 1/2"), y muestra el Área (Largo × Ancho) automáticamente.
const DIM_LABELS = { length: "Largo", width: "Ancho", height: "Alto" };
const DimensionsField = ({ value, dims, onChange }) => {
  const unit = value?.unit || "in";
  const cls = "w-full p-2 border border-gray-300 rounded-md";
  const [text, setText] = useState(() => {
    const t = {};
    (dims || []).forEach((d) => { t[d] = value?.[d] != null && !isNaN(value[d]) ? String(value[d]) : ""; });
    return t;
  });
  const setDim = (d, raw) => {
    setText((t) => ({ ...t, [d]: raw }));
    const n = parseMeasure(raw);
    onChange({ ...(value || {}), unit, [d]: isNaN(n) ? null : round(n, 4) });
  };
  const changeUnit = (u) => {
    const nv = { ...(value || {}), unit: u };
    const t = {};
    (dims || []).forEach((d) => {
      const c = value?.[d] != null && !isNaN(value[d]) ? convert(value[d], unit, u) : null;
      nv[d] = c;
      t[d] = c != null ? String(c) : "";
    });
    setText(t);
    onChange(nv);
  };
  const L = value?.length, W = value?.width;
  const area = L != null && W != null && !isNaN(L) && !isNaN(W) ? round(L * W, 2) : null;
  return (
    <div>
      <div className="flex flex-wrap items-end gap-2">
        {(dims || []).map((d) => (
          <div key={d} className="flex-1 min-w-[5rem]">
            <label className="text-xs font-semibold text-gray-500 block mb-1">{DIM_LABELS[d] || d}</label>
            <input className={cls} value={text[d] ?? ""} onChange={(e) => setDim(d, e.target.value)} placeholder="ej. 12 1/2" />
          </div>
        ))}
        <div className="w-28">
          <label className="text-xs font-semibold text-gray-500 block mb-1">Unidad</label>
          <select className={cls} value={unit} onChange={(e) => changeUnit(e.target.value)}>
            {MEASURE_UNITS.map((u) => <option key={u.id} value={u.id}>{u.label}</option>)}
          </select>
        </div>
      </div>
      <p className="text-xs text-gray-400 mt-1">
        Acepta decimales o fracciones (ej. <b>12.5</b> o <b>12 1/2</b>).
        {area != null && (
          <> · Área (Largo × Ancho): <b>{area} {unit}²</b>{unit === "in" && <> · {round(area / 144, 2)} ft²</>}{unit === "cm" && <> · {round(area / 10000, 3)} m²</>}</>
        )}
      </p>
    </div>
  );
};

const ProductModal = ({ isOpen, onClose, onSave, productData, categories, clients, materials, storage, onAddCategory, components, defaultKind = "producto", readOnly = false }) => {
  const [form, setForm] = useState({});
  const [attrs, setAttrs] = useState({});
  const [dielineFile, setDielineFile] = useState(null);
  const [imageFile, setImageFile] = useState(null);
  const [imagePreview, setImagePreview] = useState("");
  const [artFile, setArtFile] = useState(null);
  const [addingCat, setAddingCat] = useState(false);
  const [newCatName, setNewCatName] = useState("");
  const [kind, setKind] = useState("producto");           // PLAN-INTAKE-FLOW: producto | componente
  const [kindLocked, setKindLocked] = useState(false);    // al editar, el tipo se bloquea (anti-accidente)
  const [feedsInventory, setFeedsInventory] = useState(true);
  const [bom, setBom] = useState([]);                       // [{ code, qty }] solo en producto ensamblado
  const [saving, setSaving] = useState(false);
  const [qr, setQr] = useState("");
  const [zoom, setZoom] = useState("");                     // lightbox: URL de la imagen a ampliar

  // QR del SKU (solo para productos ya guardados) — trazabilidad sin error humano.
  useEffect(() => {
    if (productData?.sku) {
      QRCode.toDataURL(productQrPayload(productData), { width: 120, margin: 1 })
        .then(setQr).catch(() => setQr(""));
    } else {
      setQr("");
    }
  }, [productData]);

  useEffect(() => {
    setForm({
      name: productData?.name || "",
      code: productData?.code || "",
      categoryId: productData?.categoryId || (categories?.[0]?.id ?? ""),
      clientId: productData?.clientId || "",
      dielineNotes: productData?.dieline?.notes || "",
      dielineFileUrl: productData?.dieline?.fileUrl || "",
      active: productData?.active !== false,
    });
    setAttrs(productData?.attributes || {});
    setDielineFile(null);
    setImageFile(null);
    setImagePreview(productData?.imageUrl || "");
    setArtFile(null);
    setAddingCat(false);
    setNewCatName("");
    setKind((productData?.kind || (productData ? "producto" : defaultKind)) === "componente" ? "componente" : "producto");
    setKindLocked(!!productData); // editar = bloqueado; crear = libre
    setFeedsInventory(productData?.feedsInventory !== false);
    setBom(Array.isArray(productData?.bom) ? productData.bom.map((b) => ({ code: b.code || "", qty: b.qty || 1, previewUrl: b.previewUrl || "" })) : []);
  }, [productData, isOpen, categories]);

  // Cambiar el tipo: un producto CON componentes es un ensamblado → NO puede ser componente.
  // Un producto SIMPLE (sin BOM) sí puede reclasificarse como componente.
  const chooseKind = (k) => {
    if (k === kind) return;
    if (k === "componente" && bom.filter((b) => (b.code || "").trim()).length > 0) {
      alert("Este producto tiene componentes (BOM), así que es un producto ensamblado y no puede convertirse en componente. Quita los componentes primero, o crea el componente como un ítem nuevo.");
      return;
    }
    setKind(k);
  };

  const addBomRow = () => setBom((b) => [...b, { code: "", qty: 1, previewUrl: "" }]);
  const setBomRow = (i, k, v) => setBom((b) => b.map((r, idx) => (idx === i ? { ...r, [k]: v } : r)));
  const removeBomRow = (i) => setBom((b) => b.filter((_, idx) => idx !== i));
  // Preview por línea de BOM: pegar/soltar/subir una foto (o tomar la del componente del catálogo).
  const readBomImage = (i, file) => {
    if (!file || !file.type?.startsWith("image/")) return;
    const reader = new FileReader();
    reader.onload = (ev) => setBomRow(i, "previewUrl", ev.target.result);
    reader.readAsDataURL(file);
  };
  const matchedComp = (code) => (components || []).find((c) => (c.code || "") === (code || "").trim());

  const confirmAddCategory = async () => {
    if (!onAddCategory) return;
    const cat = await onAddCategory(newCatName);
    if (cat) { setField("categoryId", cat.id); setAddingCat(false); setNewCatName(""); }
  };

  const onPickImage = (file) => {
    if (!file || !file.type.startsWith("image/")) return;
    setImageFile(file);
    const reader = new FileReader();
    reader.onload = (ev) => setImagePreview(ev.target.result);
    reader.readAsDataURL(file);
  };

  const category = useMemo(() => getCategory(categories, form.categoryId), [categories, form.categoryId]);

  const setField = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const setAttr = (k, v) => setAttrs((a) => ({ ...a, [k]: v }));

  const handleSave = async () => {
    if (!form.name?.trim()) return alert("El producto necesita un nombre.");
    if (!form.categoryId) return alert("Elige una categoría.");
    if (!imageFile && !productData?.imageUrl) return alert("La imagen del 1UP es obligatoria (sube o pega una).");
    setSaving(true);
    try {
      let imageUrl = productData?.imageUrl || "";
      let dielineUrl = form.dielineFileUrl || "";
      let artUrl = productData?.artUrl || "";
      if (storage && imageFile) {
        const r = ref(storage, `products/images/${Date.now()}_${imageFile.name}`);
        await uploadBytes(r, imageFile);
        imageUrl = await getDownloadURL(r);
      }
      if (storage && dielineFile) {
        const r = ref(storage, `products/dielines/${Date.now()}_${dielineFile.name}`);
        await uploadBytes(r, dielineFile);
        dielineUrl = await getDownloadURL(r);
      }
      if (storage && artFile) {
        const r = ref(storage, `products/art/${Date.now()}_${artFile.name}`);
        await uploadBytes(r, artFile);
        artUrl = await getDownloadURL(r);
      }
      // BOM: se conserva SIEMPRE (no se descarta por el tipo) para no perder trabajo.
      // Sube las fotos pegadas (data URL) a Storage; deja tal cual las que ya son URL.
      let resolvedBom = [];
      for (const b of bom) {
        if (!(b.code || "").trim()) continue;
        let pv = b.previewUrl || "";
        if (storage && pv.startsWith("data:")) {
          const blob = await (await fetch(pv)).blob();
          const r = ref(storage, `products/bom/${Date.now()}_${(b.code || "comp").replace(/[^\w.-]+/g, "_")}.png`);
          await uploadBytes(r, blob);
          pv = await getDownloadURL(r);
        }
        resolvedBom.push({ code: b.code.trim(), qty: Number(b.qty) || 1, previewUrl: pv });
      }
      const data = {
        ...productData, // conserva sku/version/lastPrice en edición
        name: form.name.trim(),
        code: (form.code || "").trim(),
        kind,
        bom: resolvedBom,
        feedsInventory: kind === "componente" ? feedsInventory : true,
        categoryId: form.categoryId,
        clientId: form.clientId || null,
        imageUrl,
        artUrl,
        attributes: attrs,
        dieline: { fileUrl: dielineUrl, notes: form.dielineNotes || "" },
        active: !!form.active,
      };
      await onSave(data);
    } catch (e) {
      alert("No se pudo guardar el producto: " + e.message);
    } finally {
      setSaving(false);
    }
  };

  if (!isOpen) return null;

  const inputCls = "w-full p-2 border border-gray-300 rounded-md";
  const labelCls = "text-sm font-bold text-gray-600 block mb-1";

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex justify-center items-center z-50 p-4">
      {zoom && (
        <div className="fixed inset-0 z-[60] bg-black/80 flex items-center justify-center p-4" onClick={() => setZoom("")} title="Clic para cerrar">
          <img src={zoom} alt="preview" className="max-w-[90vw] max-h-[90vh] object-contain rounded shadow-2xl bg-white" />
        </div>
      )}
      <div className="bg-white p-6 rounded-lg shadow-xl w-full max-w-2xl max-h-[90vh] overflow-y-auto">
        <h2 className="text-2xl font-bold mb-4">{(readOnly ? "Ver " : productData ? "Editar " : "Nuevo ") + (kind === "componente" ? "Componente" : "Producto")}</h2>

        {/* SKU asignado por el sistema (read-only) + QR de trazabilidad */}
        <div className="mb-4 p-3 rounded-lg bg-gray-50 border border-gray-200 flex items-center justify-between gap-4">
          <div>
            <p className="text-xs font-semibold text-gray-500 uppercase">SKU (asignado por el sistema)</p>
            {productData?.sku ? (
              <p className="font-mono text-lg font-bold text-gray-800">{productData.sku}</p>
            ) : (
              <p className="text-sm text-gray-500 italic">Se asignará automáticamente al guardar (sin tipeo manual).</p>
            )}
          </div>
          {qr && (
            <div className="flex items-center gap-3">
              <img src={qr} alt="QR del producto" className="w-16 h-16" />
              <div className="flex flex-col gap-1">
                <button type="button" onClick={() => downloadQrPng(productQrPayload(productData), productData.sku, `${productData.sku}.png`)} className="text-xs px-2 py-1 rounded bg-gray-200 hover:bg-gray-300 font-semibold">Etiqueta PNG</button>
                <button type="button" onClick={() => downloadQrSvg(productQrPayload(productData), productData.sku, `${productData.sku}.svg`)} className="text-xs px-2 py-1 rounded bg-gray-200 hover:bg-gray-300 font-semibold">Etiqueta SVG</button>
              </div>
            </div>
          )}
        </div>

        <fieldset disabled={readOnly} className="space-y-4 border-0 p-0 m-0 min-w-0">
          <div>
            <label className={labelCls}>Tipo de ítem</label>
            {kindLocked ? (
              <div className="flex items-center gap-3">
                <span className="px-3 py-1.5 rounded-md text-sm font-semibold bg-gray-100 text-gray-700 border border-gray-300">
                  {kind === "producto" ? "Producto (SKU)" : "Componente"}
                </span>
                <button type="button" onClick={() => setKindLocked(false)} className="text-xs text-blue-600 hover:underline">cambiar tipo</button>
              </div>
            ) : (
              <select className={`${inputCls} max-w-xs`} value={kind} onChange={(e) => chooseKind(e.target.value)}>
                <option value="producto">Producto (SKU)</option>
                <option value="componente">Componente</option>
              </select>
            )}
            <p className="text-xs text-gray-400 mt-1.5">
              {kind === "producto"
                ? "Producto = lo que vendes/entregas al cliente; el sistema le asigna un SKU. Puede ser simple o armarse de componentes (BOM)."
                : "Componente = una pieza/código que arma uno o varios productos. Es reutilizable (sirve a varios productos y clientes)."}
            </p>
            {kind === "componente" && (
              <label className="flex items-start gap-2 text-sm mt-2 text-gray-700">
                <input type="checkbox" checked={feedsInventory} onChange={(e) => setFeedsInventory(e.target.checked)} className="h-4 w-4 mt-0.5" />
                <span>Se cuenta en inventario <span className="text-gray-400">— déjalo marcado si es una pieza real del trabajo. Desmárcalo solo si es un artefacto que no se inventaría (marca de registro, prueba…). Se usará al activar el inventario.</span></span>
              </label>
            )}
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className={labelCls}>Nombre *</label>
              <input className={inputCls} value={form.name || ""} onChange={(e) => setField("name", e.target.value)} placeholder="Ej. Caja RSC 12x10x8" />
            </div>
            <div>
              <label className={labelCls}>Categoría *</label>
              <select className={inputCls} value={form.categoryId || ""} onChange={(e) => setField("categoryId", e.target.value)}>
                {(categories || []).map((c) => (
                  <option key={c.id} value={c.id}>{c.name}</option>
                ))}
              </select>
              {onAddCategory && (addingCat ? (
                <div className="flex items-center gap-1 mt-1">
                  <input className={`${inputCls} text-sm`} placeholder="Nueva categoría…" value={newCatName} autoFocus
                    onChange={(e) => setNewCatName(e.target.value)}
                    onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); confirmAddCategory(); } }} />
                  <button type="button" onClick={confirmAddCategory} className="px-2 py-1.5 text-xs rounded bg-blue-600 text-white font-semibold whitespace-nowrap">Agregar</button>
                  <button type="button" onClick={() => { setAddingCat(false); setNewCatName(""); }} className="px-1 text-gray-400 hover:text-gray-600" title="Cancelar">✕</button>
                </div>
              ) : (
                <button type="button" onClick={() => setAddingCat(true)} className="text-xs text-blue-600 mt-1 hover:underline">+ Nueva categoría</button>
              ))}
            </div>
          </div>

          <div>
            <label className={labelCls}>Código (del cliente / de la pieza)</label>
            <input className={inputCls} value={form.code || ""} onChange={(e) => setField("code", e.target.value)} placeholder="Código de referencia del cliente o de la pieza (para vincular y reordenar). El SKU lo pone el sistema." />
          </div>

          <div>
            <label className={labelCls}>Cliente (opcional)</label>
            <select className={inputCls} value={form.clientId || ""} onChange={(e) => setField("clientId", e.target.value)}>
              <option value="">— Genérico (sin cliente) —</option>
              {(clients || []).map((c) => (
                <option key={c.id} value={c.id}>{c.name || c.companyName}</option>
              ))}
            </select>
          </div>

          {/* Componentes (BOM) — solo Producto. Vacío = producto simple. */}
          {kind === "producto" && (
            <fieldset className="border p-4 rounded-md">
              <legend className="text-sm font-semibold px-2">Componentes (BOM) — opcional</legend>
              <p className="text-xs text-gray-400 mb-2">Si el producto se arma de componentes, agrégalos con su cantidad por producto. Vacío = producto simple.</p>
              {bom.length > 0 && (
                <div className="space-y-2 mb-2">
                  {bom.map((r, i) => {
                    const img = r.previewUrl || matchedComp(r.code)?.imageUrl || "";
                    return (
                    <div key={i} className="flex items-center gap-2">
                      <div
                        onPaste={(e) => { for (const it of e.clipboardData.items) if (it.type.startsWith("image/")) readBomImage(i, it.getAsFile()); }}
                        onDrop={(e) => { e.preventDefault(); readBomImage(i, e.dataTransfer.files?.[0]); }}
                        onDragOver={(e) => e.preventDefault()}
                        className="relative w-20 h-20 flex-shrink-0 bg-gray-100 rounded border border-dashed border-gray-300 flex items-center justify-center overflow-hidden"
                        title="Pegar (Ctrl+V), arrastrar o hacer clic para subir la foto"
                      >
                        {img
                          ? <img src={img} alt="" className="max-h-full max-w-full object-contain pointer-events-none" />
                          : <span className="text-[10px] text-gray-400 pointer-events-none text-center px-1">pega o sube</span>}
                        <input type="file" accept="image/*" onChange={(e) => readBomImage(i, e.target.files?.[0])} className="absolute inset-0 opacity-0 cursor-pointer" title="Subir foto" />
                        {img && (
                          <span role="button" onClick={(e) => { e.stopPropagation(); setZoom(img); }} className="absolute top-0 right-0 z-10 bg-black/50 text-white rounded-bl px-1 text-[11px] leading-5 hover:bg-black/70 cursor-pointer" title="Ampliar">⤢</span>
                        )}
                      </div>
                      <input list="cb-comp-codes" className={`${inputCls} flex-1`} placeholder="Código de componente" value={r.code} onChange={(e) => setBomRow(i, "code", e.target.value)} />
                      <input type="number" min="1" className="w-24 p-2 border border-gray-300 rounded-md text-center" value={r.qty} onChange={(e) => setBomRow(i, "qty", e.target.value)} title="Cantidad por producto" />
                      <span role="button" tabIndex={0} title="Descargar QR (PNG) del componente"
                        onClick={() => { if (!(r.code || "").trim()) return; const c = matchedComp(r.code); const payload = c ? productQrPayload(c) : JSON.stringify({ t: "component", code: r.code }); downloadQrPng(payload, r.code, `${(r.code || "componente")}.png`); }}
                        className={(r.code || "").trim() ? "text-gray-400 hover:text-emerald-600 cursor-pointer" : "text-gray-300 cursor-not-allowed"}><IconQrSmall /></span>
                      <button type="button" onClick={() => removeBomRow(i)} className="px-2 text-gray-400 hover:text-red-600" title="Quitar">✕</button>
                    </div>
                    );
                  })}
                </div>
              )}
              <datalist id="cb-comp-codes">
                {(components || []).filter((c) => c.code).map((c) => <option key={c.id} value={c.code}>{c.name}</option>)}
              </datalist>
              <button type="button" onClick={addBomRow} className="text-sm text-blue-600 hover:underline">+ Agregar componente</button>
            </fieldset>
          )}

          {/* Atributos dinámicos según la categoría */}
          {category && category.attributes?.length > 0 && (
            <fieldset className="border p-4 rounded-md">
              <legend className="text-sm font-semibold px-2">Especificaciones ({category.name})</legend>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {category.attributes.map((a) => (
                  <div key={a.key} className={a.type === "dimensions" ? "md:col-span-2" : ""}>
                    <label className={labelCls}>{a.label}</label>
                    {a.type === "dimensions" ? (
                      <DimensionsField key={`${productData?.id || "new"}-${a.key}`} value={attrs[a.key]} dims={a.dims} onChange={(val) => setAttr(a.key, val)} />
                    ) : a.type === "material" ? (
                      // Conectado a la base de datos de materiales: guarda el id (ref) + el nombre (legible).
                      <select
                        className={inputCls}
                        value={attrs[`${a.key}Id`] || ""}
                        onChange={(e) => {
                          const m = (materials || []).find((mm) => mm.id === e.target.value);
                          setAttrs((prev) => ({ ...prev, [`${a.key}Id`]: e.target.value, [a.key]: m ? (m.name || "") : "" }));
                        }}
                      >
                        <option value="">— Elige material —</option>
                        {(materials || []).map((m) => <option key={m.id} value={m.id}>{m.name}{m.uniqueCode ? ` (${m.uniqueCode})` : ""}</option>)}
                      </select>
                    ) : a.type === "select" ? (
                      <select className={inputCls} value={attrs[a.key] || ""} onChange={(e) => setAttr(a.key, e.target.value)}>
                        <option value="">—</option>
                        {(a.options || []).map((o) => <option key={o} value={o}>{o}</option>)}
                      </select>
                    ) : (
                      <input
                        type={a.type === "number" ? "number" : "text"}
                        className={inputCls}
                        value={attrs[a.key] || ""}
                        onChange={(e) => setAttr(a.key, e.target.value)}
                      />
                    )}
                  </div>
                ))}
              </div>
            </fieldset>
          )}

          {/* Imagen del 1UP (OBLIGATORIA) */}
          <fieldset className="border p-4 rounded-md">
            <legend className="text-sm font-semibold px-2">Imagen del 1UP *</legend>
            <div
              onPaste={(e) => { for (const it of e.clipboardData.items) if (it.type.startsWith("image/")) onPickImage(it.getAsFile()); }}
              onDrop={(e) => { e.preventDefault(); onPickImage(e.dataTransfer.files?.[0]); }}
              onDragOver={(e) => e.preventDefault()}
              className="relative flex items-center justify-center border-2 border-dashed border-gray-300 rounded-lg h-40 bg-gray-50 hover:bg-gray-100 cursor-pointer overflow-hidden"
            >
              {imagePreview
                ? <img src={imagePreview} alt="1UP" className="max-h-full max-w-full object-contain pointer-events-none" />
                : <div className="text-center text-gray-500 text-sm px-2"><p className="font-semibold">Pegar, arrastrar o hacer clic</p><p className="text-xs">Imagen del 1UP (obligatoria)</p></div>}
              <input type="file" accept="image/*" onChange={(e) => onPickImage(e.target.files?.[0])} className="absolute inset-0 opacity-0 cursor-pointer" title="Subir imagen del 1UP" />
              {imagePreview && <span role="button" onClick={(e) => { e.stopPropagation(); setZoom(imagePreview); }} className="absolute top-1 right-1 z-10 bg-black/50 text-white rounded px-1.5 text-xs leading-5 hover:bg-black/70 cursor-pointer" title="Ampliar">⤢</span>}
            </div>
          </fieldset>

          {/* Arte 1UP (archivo de arte, opcional) — distinto del preview */}
          <fieldset className="border p-4 rounded-md">
            <legend className="text-sm font-semibold px-2">Arte 1UP</legend>
            {productData?.artUrl && !artFile && (
              <p className="text-xs text-gray-500 mb-2">Archivo actual: <a href={productData.artUrl} target="_blank" rel="noreferrer" className="text-blue-600 underline">ver</a></p>
            )}
            <input type="file" onChange={(e) => setArtFile(e.target.files?.[0] || null)} className="text-sm" />
            <p className="text-xs text-gray-400 mt-1">Archivo de arte del 1UP (PDF, AI, imagen…). Es distinto de la imagen de preview de arriba.</p>
          </fieldset>

          {/* Dieline (subir die + notas) — MVP decisión #3 */}
          <fieldset className="border p-4 rounded-md">
            <legend className="text-sm font-semibold px-2">Troquel / Dieline</legend>
            {form.dielineFileUrl && !dielineFile && (
              <p className="text-xs text-gray-500 mb-2">
                Archivo actual: <a href={form.dielineFileUrl} target="_blank" rel="noreferrer" className="text-blue-600 underline">ver</a>
              </p>
            )}
            <input type="file" onChange={(e) => setDielineFile(e.target.files?.[0] || null)} className="text-sm" />
            <textarea
              className={`${inputCls} mt-2`}
              rows={2}
              placeholder="Notas del troquel (estilo, medidas, observaciones)…"
              value={form.dielineNotes || ""}
              onChange={(e) => setField("dielineNotes", e.target.value)}
            />
          </fieldset>

          <label className="flex items-center gap-2 text-sm">
            <input type="checkbox" checked={!!form.active} onChange={(e) => setField("active", e.target.checked)} className="h-4 w-4" />
            Activo en el catálogo
          </label>
        </fieldset>

        <div className="flex justify-end gap-2 mt-6">
          <button onClick={onClose} disabled={saving} className="px-4 py-2 rounded-md bg-gray-100 hover:bg-gray-200 text-gray-700 font-semibold">{readOnly ? "Cerrar" : "Cancelar"}</button>
          {!readOnly && <button onClick={handleSave} disabled={saving} className="px-4 py-2 rounded-md bg-blue-600 hover:bg-blue-700 text-white font-bold disabled:opacity-50">{saving ? "Guardando…" : "Guardar"}</button>}
        </div>
      </div>
    </div>
  );
};

export default ProductModal;
